import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InterestedPartyUI } from 'types/policy';
import { HistoryAnswers, HistoryQuestion } from 'types/refData';
import { Nullable } from 'types/utils';
import { v4 } from 'uuid';

import { abnApi } from '../services/abn';

/**
 * @Todo
 * newsAndUpdatesConsent will come in story 11.1
 */

export interface ExtraInfo {
  historyQuestions: HistoryQuestion[];
  historyAnswers: HistoryAnswers[];
  claimCount: string;
  applied: boolean;
  businessName: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  postalSameAsBusiness: unknown;
  newsAndUpdatesConsent: boolean;
  needInterestedParties: boolean;
  interestedParties: InterestedPartyUI[];
  landlordName?: string;
  /**
   * Partner membership number for referral partners
   */
  partnerMembershipNumber?: string;
}

export enum ClaimCount {
  Zero = '0',
  One = '1',
  TwoPlus = '2+',
}

export const claimCountOptions = Object.values(ClaimCount).map((v) => ({
  text: v,
  value: v,
}));

const initialState: Nullable<ExtraInfo> = {
  historyQuestions: null,
  historyAnswers: null,
  claimCount: null,
  applied: null,
  businessName: null,
  firstName: null,
  lastName: null,
  email: null,
  mobile: null,
  postalSameAsBusiness: null,
  newsAndUpdatesConsent: false,
  needInterestedParties: false,
  interestedParties: [],
};

export const extraInfoSlice = createSlice({
  name: 'extraInfo',
  initialState,
  reducers: {
    /*
     * Redux Toolkit allows us to write "mutating" logic in reducers. It doesn't actually mutate the state
     * because it uses the Immer library, which detects changes to a "draft state" and produces a brand new
     * immutable state based off those changes
     */
    updateHistoryQuestions: (state, action: PayloadAction<HistoryQuestion[] | null>) => {
      state.historyQuestions = action.payload;
    },
    updateHistoryAnswers: (state, action: PayloadAction<HistoryAnswers[] | null>) => {
      state.historyAnswers = action.payload;
    },
    updateClaimCount: (state, action: PayloadAction<string>) => {
      state.claimCount = action.payload;
    },
    updateApplied: (state, action: PayloadAction<boolean | null>) => {
      state.applied = action.payload;
    },
    updateBusinessName: (state, action: PayloadAction<string | null>) => {
      state.businessName = action.payload;
    },
    updateFirstName: (state, action: PayloadAction<string | null>) => {
      state.firstName = action.payload;
    },
    updateLastName: (state, action: PayloadAction<string | null>) => {
      state.lastName = action.payload;
    },
    updateMobile: (state, action: PayloadAction<string | null>) => {
      state.mobile = action.payload;
    },
    updateEmail: (state, action: PayloadAction<string | null>) => {
      state.email = action.payload;
    },
    updateNewsAndUpdatesConsent: (state, action: PayloadAction<boolean | null>) => {
      state.newsAndUpdatesConsent = action.payload;
    },
    updateNeedInterestedParties: (state, action: PayloadAction<boolean>) => {
      state.needInterestedParties = action.payload;
    },
    addInterestedParty: (state, action: PayloadAction<Omit<InterestedPartyUI, 'id'>>) => {
      const interestedParty = { id: v4(), ...action.payload };
      state.interestedParties?.push(interestedParty);
    },
    updateInterestedParty: (state, action: PayloadAction<InterestedPartyUI>) => {
      if (state.interestedParties) {
        const interestedParty = state.interestedParties.find(({ id }) => id === action.payload.id);
        if (interestedParty) {
          state.interestedParties = state.interestedParties.map((ip) =>
            ip.id === action.payload.id ? action.payload : ip
          );
        }
      }
    },
    updateLandlordName: (state, action: PayloadAction<string | undefined>) => {
      state.landlordName = action.payload;
    },
    removeInterestedParty: (state, action: PayloadAction<string>) => {
      if (state.interestedParties) {
        const interestedParty = state.interestedParties.find(({ id }) => id === action.payload);
        if (interestedParty) {
          state.interestedParties.splice(state.interestedParties.indexOf(interestedParty), 1);
        }
      }
    },
    updatePartnerMembershipNumber: (state, action: PayloadAction<string | undefined>) => {
      state.partnerMembershipNumber = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(abnApi.endpoints.abnLookup.matchFulfilled, (state, { payload }) => {
      state.businessName = payload;
    });
  },
});

/* Fully typed Action creators are automagically generated for each case reducer function */
export const {
  updateHistoryQuestions,
  updateHistoryAnswers,
  updateClaimCount,
  updateApplied,
  updateBusinessName,
  updateFirstName,
  updateLastName,
  updateMobile,
  updateEmail,
  updateNewsAndUpdatesConsent,
  updateNeedInterestedParties,
  updateLandlordName,
  addInterestedParty,
  updateInterestedParty,
  removeInterestedParty,
  updatePartnerMembershipNumber,
} = extraInfoSlice.actions;

export default extraInfoSlice.reducer;
